import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import CTAButton from '../components/CTAButton';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiUsers, FiAward, FiHeart } = FiIcons;

const AboutPage = () => {
  const values = [
    {
      icon: FiShield,
      title: "Protection First",
      description: "We prioritize comprehensive coverage that truly protects your business when you need it most."
    },
    {
      icon: FiUsers,
      title: "Customer Focus",
      description: "Every client receives personalized service and expert guidance tailored to their unique business needs."
    },
    {
      icon: FiAward,
      title: "Excellence",
      description: "We partner with top-rated insurers like Next Insurance to deliver superior coverage and service."
    },
    {
      icon: FiHeart,
      title: "Community",
      description: "Supporting California small businesses is our passion and the heart of everything we do."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2084&q=80)'
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              About Burbank Insurance Services
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
            >
              Your trusted partner for Next Insurance solutions in California. Protecting small businesses with affordable, comprehensive coverage since day one.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                At Burbank Insurance Services, we believe every California small business deserves access to affordable, comprehensive insurance protection. We've partnered with Next Insurance to bring you cutting-edge coverage that's fast, affordable, and tailored to your unique needs.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Whether you're a contractor in Los Angeles, a restaurant owner in San Diego, or a retailer in San Francisco, we're here to help you find the perfect coverage to protect your business, your employees, and your dreams.
              </p>
              <CTAButton text="Get Your Quote Today" />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-primary-50 p-8 rounded-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Us?</h3>
              <ul className="space-y-4 text-lg text-gray-700">
                <li>• 100% online quote process</li>
                <li>• No agent appointments needed</li>
                <li>• Instant policy issuance</li>
                <li>• Competitive Next Insurance rates</li>
                <li>• Expert California coverage guidance</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core principles guide everything we do and every recommendation we make.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={value.icon} className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Next Insurance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why We Partner with Next Insurance
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Technology-Driven</h3>
              <p className="text-gray-600">
                Next Insurance uses cutting-edge technology to provide instant quotes and coverage in minutes, not days or weeks.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Affordable Coverage</h3>
              <p className="text-gray-600">
                With competitive rates starting at just $19/month, Next Insurance makes professional coverage accessible to businesses of all sizes.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Trusted by 600,000+</h3>
              <p className="text-gray-600">
                Over 600,000 small businesses trust Next Insurance for their coverage needs, with a 4.7/5 customer satisfaction rating.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Ready to Experience the Burbank Insurance Difference?
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl mb-8"
          >
            Join thousands of California businesses who trust us for their insurance needs. Get your instant quote today.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <CTAButton 
              text="Get Your Instant Quote Now" 
              size="xl" 
              className="bg-white text-primary-600 hover:bg-gray-100" 
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;